import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    GoogleMap,
    Marker,
    Circle,
} from "@react-google-maps/api";
import Loader from '../common/loader/Loader';

const containerStyle = {
    width: "100%",
    height: "100%",
};

interface MapViewProps {
    latitude: string | null;
    longitude: string | null;
    radius: number;
}

const DEFAULT_US_CENTER = {
    lat: 39.8283, // Center of continental US
    lng: -98.5795
};

const MapView = ({ latitude, longitude, radius }: MapViewProps) => {
    const [loading, setLoading] = useState(true);
    const mapRef = useRef<google.maps.Map | null>(null);
    const hasZoomedRef = useRef(false);

    const isValidCoords =
        latitude !== null &&
        longitude !== null &&
        !isNaN(parseFloat(latitude)) &&
        !isNaN(parseFloat(longitude));

    const center = isValidCoords
        ? { lat: parseFloat(latitude), lng: parseFloat(longitude) }
        : DEFAULT_US_CENTER;

    const onMapLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
        setLoading(false);
    }, []);

    const RadiusInMeters: number = radius * 1609.34;

    const smoothZoom = (map: google.maps.Map, targetZoom: number) => {
        let currentZoom = map.getZoom() || 10;
        const step = targetZoom > currentZoom ? 1 : -1;

        const zoomInterval = setInterval(() => {
            if (currentZoom === targetZoom) {
                clearInterval(zoomInterval);
                return;
            }
            currentZoom += step;
            map.setZoom(currentZoom);
        }, 100);
    };

    useEffect(() => {
        if (mapRef.current && !hasZoomedRef.current) {
            const map = mapRef.current;
            map.panTo(center);
            smoothZoom(map, 14);
            hasZoomedRef.current = true;
        }
    }, [center]);

    return (
        <div style={{ height: "100%", width: '100%' }}>
            {loading && <Loader />}
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={13}
                onLoad={onMapLoad}
            >
                {isValidCoords && <Marker position={center} />}
                {(radius && isValidCoords) && (
                    <Circle
                        center={center}
                        radius={RadiusInMeters}
                        options={{
                            strokeColor: "blue",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "blue",
                            fillOpacity: 0.35,
                        }}
                    />
                )}
            </GoogleMap>
        </div>
    );
}

export default MapView;
